import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  beginLocationsContext,
  endLocationsContext,
} from 'src/state/slices/locations';
import { METROBI_TIMING } from './metrobi';
import { isDefined } from 'src/utils';
import {
  Autocomplete,
  Button,
  NativeSelect,
  Spacer,
  TextInput,
} from '@odekoteam/doppio';
import SearchSvg from 'assets/svgs/search.svg';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { TableVirtuoso } from 'react-virtuoso';
import { BulkEditTab } from 'src/components/Modal/BulkEditLocationsModal';
import { TableHeaderCells } from 'src/components/Table';
import { GET_COHORTS } from 'src/graphql/requests/getCohorts';
import { GET_LOCATIONS } from 'src/graphql/requests/getLocations';
import { GetLocation } from 'src/graphql/types/delivery';
import {
  GGetCohortsQuery,
  GGetLocationsQuery,
  GScheduledDeliveryTimeSourceEnum,
} from 'src/graphql/types/generated';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import {
  selectedWarehouseSelector,
  warehousesSelector,
} from 'src/state/selectors/delivery';
import { setSelectedWarehouseId } from 'src/state/slices/delivery';
import { ModalType, showModal } from 'src/state/slices/modal';
import {
  readManageLocationsHandoff,
  removeManageLocationsHandoff,
} from 'src/utils/locations';
import { Flex } from 'theme-ui';
import { useClient, useQuery } from 'urql';
import { useDebouncedValue } from 'src/hooks/useDebouncedValue';
import { GET_CUSTOMER_LOCATION_FILTER_OPTIONS } from 'src/graphql/pending/customerLocationFilters';
import {
  GRouteManagementGetCustomerLocationFilterOptionsQuery,
  GRouteManagementGetCustomerLocationFilterOptionsQueryVariables,
} from 'src/graphql/types/customerLocationFilters.generated';
import { LoaderSpinner } from '../Loader';
import { tableStyle } from '../Table/style';
import DownloadLocationsCsv from './DownloadLocationsCsv';
import Loader from './Loader';
import LocationRow from './LocationRow';
import {
  GetLocationsVariables,
  LOCATIONS_PAGE_SIZE,
  fetchAllCustomerLocations,
} from './fetchCustomerLocations';
import {
  Item,
  MetrobiFilter,
  metrobiFilterItems,
  RoutingFilter,
  allCitiesItem,
  allCohortsItem,
  allStatesItem,
  buildCustomerLocationsInput,
  matchesEditableFilters,
  noCohortsItem,
  routingFilterItems,
  toItems,
  uniqueItems,
} from './utils';

const labels = [
  '<select>',
  'Location Id',
  'Location',
  'City',
  'State',
  'Zip Code',
  'Cohort',
  'Service Time',
  'Automatic Routing',
  'Enforce Item Scanning',
  'Deliver Within Store Hours',
  'Customer Pickup',
  'Print Delivery Sheet',
  'Include Financials on Delivery Sheet',
  'Store Hours Confirmed',
  'Scheduled Delivery Time',
];

type GetCohortsVariables = {
  routePlanInput: { warehouseId?: string; nightEnd?: string };
};

// Search and zip are free text; wait for typing to settle before re-querying.
const FILTER_DEBOUNCE_MS = 300;

const NO_LOCATIONS: GetLocation[] = [];

const table = ({
  style,
  ...props
}: React.TableHTMLAttributes<HTMLTableElement>): JSX.Element => (
  <table {...props} style={{ ...style, width: '96vw' }} />
);

// This existing screen coordinates pagination, handoffs, filters and warehouse state.
// eslint-disable-next-line complexity
const WarehouseLocationsTable = (): JSX.Element => {
  const { deliveryServiceMetrobiEligibility = false } = useFlags();
  const contextId = useId();
  const metrobi = useAppSelector((state) => state.locations.metrobi);
  const routingLocations = useAppSelector(
    (state) => state.locations.routingLocations,
  );
  const [metrobiFilter, setMetrobiFilter] = useState(MetrobiFilter.All);
  const tableLabels = labels.flatMap((label) =>
    deliveryServiceMetrobiEligibility && label === 'Store Hours Confirmed'
      ? ['Metrobi Eligible', label]
      : [label],
  );
  const dispatch = useAppDispatch();

  const selectedWarehouse = useAppSelector(selectedWarehouseSelector);
  const warehouses = useAppSelector(warehousesSelector);
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.toString();
  const handoff = useMemo(
    () => readManageLocationsHandoff(new URLSearchParams(search)),
    [search],
  );
  const handledHandoffRef = useRef<string>();
  const warehouseSwitchHandoffRef = useRef<string>();
  const client = useClient();

  const [selectedLocations, setSelectedLocations] = useState<GetLocation[]>([]);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [selectAllError, setSelectAllError] = useState(false);
  const [handoffMessage, setHandoffMessage] = useState<string>();
  // filters
  const [searchValue, setSearchValue] = useState('');
  const [routingFilter, setRoutingFilter] = useState(
    RoutingFilter.allLocations,
  );
  const [stateFilter, setStateFilter] = useState(allStatesItem.value);
  const [cityFilter, setCityFilter] = useState(allCitiesItem.value);
  const [zipFilter, setZipFilter] = useState('');
  const [cohortFilter, setCohortFilter] = useState(allCohortsItem.value);
  // dropdown inputs
  const [cohortItems, setCohortItems] = useState<Item[]>([
    allCohortsItem,
    noCohortsItem,
  ]);
  const handoffWarehouseMatches =
    !handoff.isPresent ||
    (handoff.isValid && selectedWarehouse?.id === handoff.warehouseId);

  const [
    {
      data: cohortData,
      fetching: cohortFetching,
      error: cohortError,
      operation: cohortOperation,
    },
  ] = useQuery<GGetCohortsQuery, GetCohortsVariables>({
    query: GET_COHORTS,
    variables: {
      routePlanInput: {
        warehouseId: selectedWarehouse?.id,
        nightEnd: selectedWarehouse?.currentNightEnd,
      },
    },
    pause: !selectedWarehouse?.id || !handoffWarehouseMatches,
  });

  const debouncedSearchValue = useDebouncedValue(
    searchValue,
    FILTER_DEBOUNCE_MS,
  );
  const debouncedZipFilter = useDebouncedValue(zipFilter, FILTER_DEBOUNCE_MS);
  const filterInput = useMemo(
    () =>
      buildCustomerLocationsInput({
        warehouseId: selectedWarehouse?.id,
        searchValue: debouncedSearchValue,
        routingFilter,
        stateFilter,
        cityFilter,
        zipFilter: debouncedZipFilter,
        cohortFilter,
        cohorts: cohortData?.routingImportPlan?.cohorts,
        metrobiFilter: deliveryServiceMetrobiEligibility
          ? metrobiFilter
          : MetrobiFilter.All,
      }),
    [
      selectedWarehouse?.id,
      debouncedSearchValue,
      routingFilter,
      stateFilter,
      cityFilter,
      debouncedZipFilter,
      cohortFilter,
      cohortData,
      deliveryServiceMetrobiEligibility,
      metrobiFilter,
    ],
  );
  // Loaded pages and the cursor read from them are tagged with the filters they belong
  // to, so any filter change (including back to an earlier one) starts over at the first
  // page without a reset render in between.
  const filterKey = JSON.stringify(filterInput);
  const filterKeyRef = useRef(filterKey);
  filterKeyRef.current = filterKey;
  const [loadedPages, setLoadedPages] = useState<{
    filterKey: string;
    locations: GetLocation[];
    totalCount: number;
    after?: string;
  }>({ filterKey, locations: NO_LOCATIONS, totalCount: 0 });
  const loadedPagesMatchFilters = loadedPages.filterKey === filterKey;
  const after = loadedPagesMatchFilters ? loadedPages.after : undefined;
  const allLocations = loadedPagesMatchFilters
    ? loadedPages.locations
    : NO_LOCATIONS;
  const totalCount = loadedPagesMatchFilters ? loadedPages.totalCount : 0;

  const [{ data, fetching, error, operation: locationsOperation }] = useQuery<
    GGetLocationsQuery,
    GetLocationsVariables
  >({
    query: GET_LOCATIONS,
    requestPolicy: 'network-only',
    variables: {
      input: filterInput,
      first: LOCATIONS_PAGE_SIZE,
      after,
    },
    pause: !selectedWarehouse?.id || !handoffWarehouseMatches,
  });
  const locationsDataMatchesFilters =
    !locationsOperation?.variables ||
    (JSON.stringify(locationsOperation.variables.input) === filterKey &&
      locationsOperation.variables.after === after);
  const currentPage = locationsDataMatchesFilters
    ? data?.customerLocations
    : undefined;
  const nextCursor = currentPage?.pageInfo.hasNextPage
    ? currentPage.pageInfo.endCursor
    : undefined;
  const cohortDataMatchesWarehouse =
    !cohortOperation?.variables ||
    cohortOperation.variables.routePlanInput.warehouseId ===
      selectedWarehouse?.id;

  const [{ data: filterOptionsData }] = useQuery<
    GRouteManagementGetCustomerLocationFilterOptionsQuery,
    GRouteManagementGetCustomerLocationFilterOptionsQueryVariables
  >({
    query: GET_CUSTOMER_LOCATION_FILTER_OPTIONS,
    variables: { warehouseId: selectedWarehouse?.id ?? '' },
    pause: !selectedWarehouse?.id || !handoffWarehouseMatches,
  });
  const filterOptions = filterOptionsData?.customerLocationFilterOptions;
  const stateItems = useMemo(
    () => [allStatesItem, ...toItems(filterOptions?.states ?? [])],
    [filterOptions],
  );
  const cityItems = useMemo(
    () => [allCitiesItem, ...toItems(filterOptions?.cities ?? [])],
    [filterOptions],
  );
  const zipItems = useMemo(
    () => toItems(filterOptions?.zips ?? []),
    [filterOptions],
  );

  useLayoutEffect(() => {
    dispatch(beginLocationsContext(contextId));
    return (): void => {
      dispatch(endLocationsContext(contextId));
    };
  }, [contextId, dispatch]);

  // Overlay edits made on this page since the rows were fetched (routing settings,
  // Metrobi), both for the table and for on-demand full fetches.
  const withLocalState = useCallback(
    (locations: GetLocation[]): GetLocation[] => {
      const settingsById = new Map(
        routingLocations.map((settings) => [settings.locationId, settings]),
      );
      return locations.map((location) => {
        const settings = settingsById.get(location.id);
        return {
          ...location,
          ...settings,
          scheduledDeliveryTimeSource: (settings?.scheduledDeliveryTimeSource ??
            location.scheduledDeliveryTimeSource) as GScheduledDeliveryTimeSourceEnum,
          ...(metrobi?.contextId === contextId
            ? metrobi.locations[location.id]
            : undefined),
        };
      });
    },
    [routingLocations, metrobi, contextId],
  );
  // Rows arrive filtered and ordered by the server; only loaded pages are held here.
  const filteredLocations = useMemo(
    () =>
      withLocalState(allLocations).filter((location) =>
        matchesEditableFilters(location, filterInput),
      ),
    [allLocations, withLocalState, filterInput],
  );
  // Every location matching the filters (optionally only among `ids`), across all pages.
  const fetchAllFilteredLocations = useCallback(
    async (ids?: string[]): Promise<GetLocation[]> =>
      withLocalState(
        await fetchAllCustomerLocations(client, { ...filterInput, ids }),
      ).filter((location) => matchesEditableFilters(location, filterInput)),
    [client, filterInput, withLocalState],
  );

  // A filter change keeps only the selected locations that still match it. The selection
  // can span pages that are not loaded, so ask the server which selected ids match.
  const selectedLocationsRef = useRef(selectedLocations);
  selectedLocationsRef.current = selectedLocations;
  // Bumped by every filter change and header toggle; an in-flight select-all only applies
  // if nothing superseded it.
  const selectAllRequestRef = useRef(0);
  useEffect(() => {
    selectAllRequestRef.current += 1;
    setIsSelectingAll(false);
    setIsAllSelected(false);
    setSelectAllError(false);
    const selectedIds = selectedLocationsRef.current.map(
      (location) => location.id,
    );
    if (selectedIds.length === 0) return;
    const prunedIds = new Set(selectedIds);
    fetchAllFilteredLocations(selectedIds)
      .then((locations) => {
        if (filterKeyRef.current !== filterKey) return;
        const matchingIds = new Set(locations.map((location) => location.id));
        // Only drop ids this check covered; selections made meanwhile stay.
        const keep = (location: GetLocation): boolean =>
          !prunedIds.has(location.id) || matchingIds.has(location.id);
        setSelectedLocations((current) => current.filter(keep));
      })
      .catch(() => {
        if (filterKeyRef.current === filterKey) setSelectedLocations([]);
      });
    // Runs once per filter set; fetchAllFilteredLocations changes with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // A selected row edited out of the current filters leaves the selection, as it leaves
  // the table.
  const effectiveSelectedLocations = useMemo(
    () =>
      withLocalState(selectedLocations).filter((location) =>
        matchesEditableFilters(location, filterInput),
      ),
    [selectedLocations, withLocalState, filterInput],
  );
  // Drop those rows from the selection itself, so a later filter change cannot bring
  // them back.
  useEffect(() => {
    if (effectiveSelectedLocations.length === selectedLocations.length) return;
    const effectiveIds = new Set(
      effectiveSelectedLocations.map((location) => location.id),
    );
    setSelectedLocations((current) =>
      current.filter((location) => effectiveIds.has(location.id)),
    );
  }, [effectiveSelectedLocations, selectedLocations]);

  // Select-all covers every location matching the filters, not just the loaded pages.
  const onHeaderSelected = (value: boolean): void => {
    selectAllRequestRef.current += 1;
    const request = selectAllRequestRef.current;
    const isLatest = (): boolean => selectAllRequestRef.current === request;
    setSelectAllError(false);
    if (!value) {
      setIsSelectingAll(false);
      setSelectedLocations([]);
      return;
    }
    setIsSelectingAll(true);
    fetchAllFilteredLocations()
      .then((locations) => {
        if (!isLatest()) return;
        setSelectedLocations(
          locations.filter((location) =>
            isDefined(location.includeInAutomatedRouting),
          ),
        );
      })
      .catch(() => {
        if (!isLatest()) return;
        setSelectAllError(true);
        setIsAllSelected(false);
      })
      .finally(() => {
        if (isLatest()) setIsSelectingAll(false);
      });
  };

  const onUpdateLocationsButtonClicked = (): void => {
    dispatch(
      showModal({
        type: ModalType.BulkEditLocationsModal,
        dismissButtonLabel: 'Cancel',
        variables: {
          locations: effectiveSelectedLocations,
          metrobiContextId: contextId,
          cohorts: cohortData?.routingImportPlan?.cohorts ?? [],
        },
      }),
    );
  };

  const itemContent = (
    _index: number,
    location?: GetLocation,
  ): JSX.Element | null => {
    if (location)
      return (
        <LocationRow
          location={location}
          showMetrobi={deliveryServiceMetrobiEligibility}
          contextId={contextId}
          searchValue={searchValue}
          setSelectedLocations={setSelectedLocations}
          selectedLocations={effectiveSelectedLocations}
          cohorts={cohortData?.routingImportPlan?.cohorts}
        />
      );
    return null;
  };
  const fixedHeaderContent = (): JSX.Element => (
    <TableHeaderCells
      labels={tableLabels}
      onHeaderSelected={onHeaderSelected}
      isAllSelected={isAllSelected}
      setIsAllSelected={setIsAllSelected}
    />
  );

  // Append each page as it arrives. A map prevents cached pages or repeated responses
  // from duplicating rows; insertion order keeps the server's ordering.
  useEffect(() => {
    if (!currentPage) return;
    const { nodes = [], totalCount: currentTotalCount } = currentPage;
    setLoadedPages((current) => {
      const sameFilters = current.filterKey === filterKey;
      const byId = new Map(
        (sameFilters ? current.locations : []).map(
          (location) => [location.id, location] as const,
        ),
      );
      nodes.forEach((location) => {
        if (location) byId.set(location.id, location);
      });
      return {
        filterKey,
        locations: Array.from(byId.values()),
        totalCount: currentTotalCount,
        after: sameFilters ? current.after : undefined,
      };
    });
  }, [currentPage, filterKey]);

  const loadNextPage = (): void => {
    if (!nextCursor || fetching || nextCursor === after) return;
    setLoadedPages((current) =>
      current.filterKey === filterKey
        ? { ...current, after: nextCursor }
        : current,
    );
  };

  const consumeHandoff = useCallback(
    (message?: string): void => {
      handledHandoffRef.current = search;
      setHandoffMessage(message);
      setSearchParams(removeManageLocationsHandoff(searchParams), {
        replace: true,
      });
    },
    [search, searchParams, setSearchParams],
  );

  // Align the global warehouse before loading or resolving the handoff. Invalid
  // input is consumed without changing the user's current warehouse.
  useEffect(() => {
    if (!handoff.isPresent) {
      handledHandoffRef.current = undefined;
      warehouseSwitchHandoffRef.current = undefined;
      return;
    }
    if (handledHandoffRef.current === search) return;
    if (!handoff.isValid) {
      consumeHandoff('The selected locations link is invalid.');
      return;
    }
    if (!warehouses) return;

    const requestedWarehouse = warehouses.find(
      (warehouse) => warehouse.id === handoff.warehouseId,
    );
    if (!requestedWarehouse) {
      consumeHandoff('The originating warehouse could not be found.');
      return;
    }
    if (selectedWarehouse?.id !== requestedWarehouse.id) {
      if (warehouseSwitchHandoffRef.current === search) return;
      warehouseSwitchHandoffRef.current = search;
      dispatch(setSelectedWarehouseId({ warehouseId: requestedWarehouse.id }));
    } else {
      warehouseSwitchHandoffRef.current = undefined;
    }
  }, [
    handoff,
    search,
    warehouses,
    selectedWarehouse?.id,
    dispatch,
    consumeHandoff,
  ]);

  const currentPageMerged =
    currentPage?.nodes?.every(
      (location) =>
        location === null ||
        allLocations.some(
          (loadedLocation) => loadedLocation.id === location.id,
        ),
    ) ?? false;
  const loadedPagesSettled = Boolean(
    currentPage && !fetching && currentPageMerged,
  );
  const cohortsLoaded = Boolean(
    cohortData && cohortDataMatchesWarehouse && !cohortFetching,
  );

  // Fetch exactly the requested ids, ignoring table filters, then consume the URL before
  // opening the existing bulk editor.
  const handoffRequestRef = useRef<string>();
  useEffect(() => {
    if (
      !handoff.isPresent ||
      !handoff.isValid ||
      handledHandoffRef.current === search ||
      handoffRequestRef.current === search ||
      selectedWarehouse?.id !== handoff.warehouseId
    ) {
      return;
    }
    if (cohortError) {
      consumeHandoff('The selected locations could not be loaded.');
      return;
    }
    if (!cohortsLoaded) return;

    handoffRequestRef.current = search;
    const requestedIds = new Set(handoff.locationIds);
    fetchAllCustomerLocations(client, {
      warehouseId: handoff.warehouseId,
      ids: Array.from(requestedIds),
    })
      .then((locations) => {
        const matchedById = new Map(
          withLocalState(locations)
            .filter((location) => requestedIds.has(location.id))
            .map((location) => [location.id, location] as const),
        );
        const matchedLocations = Array.from(matchedById.values());
        const unresolvedCount = requestedIds.size - matchedLocations.length;
        const unresolvedMessage =
          unresolvedCount > 0
            ? `${unresolvedCount} ${
                unresolvedCount === 1 ? 'location' : 'locations'
              } could not be found.`
            : undefined;

        consumeHandoff(unresolvedMessage);
        setIsAllSelected(false);
        setSelectedLocations(matchedLocations);
        if (matchedLocations.length === 0) return;

        dispatch(
          showModal({
            type: ModalType.BulkEditLocationsModal,
            dismissButtonLabel: 'Cancel',
            variables: {
              locations: matchedLocations,
              metrobiContextId: contextId,
              cohorts: cohortData?.routingImportPlan?.cohorts ?? [],
              initialTab: BulkEditTab.AutomatedRouting,
            },
          }),
        );
      })
      .catch(() => {
        consumeHandoff('The selected locations could not be loaded.');
      })
      .finally(() => {
        handoffRequestRef.current = undefined;
      });
  }, [
    handoff,
    search,
    selectedWarehouse?.id,
    cohortError,
    cohortsLoaded,
    client,
    withLocalState,
    contextId,
    cohortData,
    dispatch,
    consumeHandoff,
  ]);

  // State, city and zip options come from the server; cohorts from the routing plan.
  useEffect(() => {
    const myCohortItems =
      cohortData?.routingImportPlan?.cohorts
        ?.map(
          (cohort): Item => ({
            value: cohort.id,
            label: cohort.name,
          }),
        )
        .filter(uniqueItems) ?? [];
    setCohortItems([allCohortsItem, ...myCohortItems, noCohortsItem]);
  }, [cohortData]);

  return (
    <Flex
      id="locations-table"
      tabIndex={-1}
      sx={{ flexDirection: 'column', width: '100vw', marginTop: '20px' }}
    >
      <Flex
        sx={{
          flexDirection: ['column', 'column', 'column', 'row'],
          justifyContent: 'space-between',
          marginBottom: '14px',
          width: '96vw',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <Flex>
          <Button
            id="update-locations-button"
            isDisabled={
              effectiveSelectedLocations.length === 0 ||
              isSelectingAll ||
              Boolean(metrobi?.pendingIds.length) ||
              Boolean(metrobi?.customerPickupPending)
            }
            onPress={onUpdateLocationsButtonClicked}
          >
            {isSelectingAll
              ? 'Selecting locations...'
              : `Edit ${effectiveSelectedLocations.length} locations`}
          </Button>
          <Spacer width="$1" />
          <TextInput
            iconLeft={<SearchSvg />}
            id="location-search"
            onChangeText={(value): void => setSearchValue(value)}
            placeholder="Search"
            value={searchValue}
          />
        </Flex>
        <Spacer height="$1" />
        <Flex sx={{ flexDirection: ['column', 'column', 'row'] }}>
          <NativeSelect
            id="routing-input-controlled"
            items={routingFilterItems}
            value={routingFilter}
            onValueChange={(value): void =>
              setRoutingFilter(value as RoutingFilter)
            }
          />
          <Spacer width="$1" />
          <NativeSelect
            id="cohort-input-controlled"
            items={cohortItems}
            value={cohortFilter}
            onValueChange={(value): void => setCohortFilter(value as string)}
          />
          <Spacer width="$1" />
          <NativeSelect
            id="state-input-controlled"
            items={stateItems}
            value={stateFilter}
            onValueChange={(value): void => setStateFilter(value as string)}
          />
          <Spacer width="$1" />
          <NativeSelect
            id="city-input-controlled"
            items={cityItems}
            value={cityFilter}
            onValueChange={(value): void => setCityFilter(value as string)}
          />
          <Spacer width="$1" />
          <Autocomplete
            id="zip-input-controlled"
            options={zipItems}
            value={zipFilter}
            placeholder="All Zip Codes"
            onChangeText={(value): void => setZipFilter(value)}
            onSelect={(option): void => setZipFilter(option.value as string)}
          />
          {deliveryServiceMetrobiEligibility && (
            <>
              <Spacer width="$1" />
              <NativeSelect
                id="metrobi-eligibility-filter"
                aria-label="Metrobi eligibility"
                value={metrobiFilter}
                items={metrobiFilterItems}
                onValueChange={(value): void =>
                  setMetrobiFilter(value as MetrobiFilter)
                }
              />
            </>
          )}
        </Flex>
      </Flex>
      {deliveryServiceMetrobiEligibility && (
        <div sx={{ marginBottom: '12px' }}>
          <div role="status" tabIndex={-1} id="metrobi-update-status">
            {metrobi?.contextId === contextId && metrobi.message && (
              <>
                {metrobi.message} {METROBI_TIMING}
              </>
            )}
          </div>
        </div>
      )}
      {error && (
        <div role="alert">
          Locations could not be completely loaded. Reload the page to retry.
        </div>
      )}
      {selectAllError && (
        <div role="alert">
          The matching locations could not be selected. Try again.
        </div>
      )}
      {handoffMessage && (
        <div
          role="alert"
          data-test-id="manage-locations-handoff-message"
          sx={{
            variant: 'text.paragraphSmDefault',
            color: 'secondaryText',
            marginBottom: '10px',
          }}
        >
          {handoffMessage}
        </div>
      )}
      <Flex
        sx={{
          variant: 'text.paragraphLgDefault',
          marginBottom: '6px',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '96vw',
        }}
      >
        <span>
          {allLocations.length} of {totalCount} locations loaded
          {!loadedPagesSettled && (
            <LoaderSpinner
              imgStyle={{
                top: 'auto',
                left: 'auto',
                height: '20px',
                margin: '2px 10px',
              }}
            />
          )}
        </span>
        <DownloadLocationsCsv
          loadLocations={fetchAllFilteredLocations}
          showMetrobi={deliveryServiceMetrobiEligibility}
          disabled={!cohortsLoaded || Boolean(cohortError)}
        />
      </Flex>
      {fetching && allLocations.length === 0 ? (
        <Loader labels={tableLabels} />
      ) : (
        <TableVirtuoso
          sx={{ ...tableStyle }}
          data={filteredLocations}
          computeItemKey={(_index, location): string => location.id}
          useWindowScroll
          fixedHeaderContent={fixedHeaderContent}
          itemContent={itemContent}
          endReached={loadNextPage}
          components={{
            Table: table,
          }}
        />
      )}
    </Flex>
  );
};

const LocationsTable = (): JSX.Element => {
  const warehouse = useAppSelector(selectedWarehouseSelector);
  // Remount all filters, selections and request state with the warehouse.
  return <WarehouseLocationsTable key={warehouse?.id ?? 'no-warehouse'} />;
};

export default LocationsTable;
